import {useState} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {FormHelperText, withStyles} from '@material-ui/core'
import {ClassNameMap} from '@material-ui/core/styles/withStyles'
import {useHistory, useParams} from 'react-router-dom'
import styled from '@emotion/styled'
import messages from '../../../../assets/messages'
import {isMobile} from '../../../../common/utils'
import LoadingButton from '../../../Common/LoadingButton'
import {PammTermsCheckbox} from '../../../Common/PammTermsCheckbox'
import {backendRequest} from '../../../../common/utils/requests'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0 0 0 2rem;
`

const Header = styled.div`
  font-family: Roboto;
  font-size: 1.375rem;
`

const Description = styled.div`
  color: #73738C;
  font-family: Roboto;
  font-size: 0.875rem;
`

const Info = styled.div`
  background: #E8F7FE;
  border-radius: 0.5rem;
  color: #1C212E;
  font-family: Roboto;
  font-size: 0.875rem;
  padding: 1rem;
`

const ButtonsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const styles = (theme) => ({
  errorMessage: {
    color: theme.palette.error.main,
    display: 'inline-block',
    margin: '0.5rem 0',
    verticalAlign: 'bottom',
  },
})

type DecideFormProps = {
  classes?: Partial<ClassNameMap<keyof ReturnType<typeof styles>>>
  disabled?: boolean
} & WithNamespaces

/**
 * Replaces the legacy PasswordForm. Both buttons accept the offer — the
 * server auto-generates an MT account password (PT-5354) — and redirect to
 * the accounts list. "Decide later" exists per AC #3 to give the user an
 * obvious off-ramp; functionally it's the same call so the offer ends up in
 * their accounts either way.
 */
const _DecideForm = ({classes, disabled, t}: DecideFormProps) => {
  const {pammManagerId, pammOfferId} = useParams<{pammManagerId: string; pammOfferId: string}>()
  const history = useHistory()
  const [loadingPrimary, setLoadingPrimary] = useState(false)
  const [loadingSecondary, setLoadingSecondary] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)

  const acceptOffer = async (setLoading: (value: boolean) => void) => {
    setLoading(true)
    setSubmitError('')
    try {
      const response = await backendRequest(
        `/api/v2/dashboard/pamm/managers/${Number(pammManagerId)}/offers/${Number(pammOfferId)}/accept`,
        {},
      )
      const {errorCode} = await response.json()
      if (response.ok && !errorCode) {
        history.push('/accounts')
        return
      }
      // The server returns a stable code, not a user-facing message (PT-7336): translate it here.
      setSubmitError(
        errorCode
          ? t(messages.pammOfferAcceptError.i18nKey, messages.pammOfferAcceptError.defaults)
          : t(messages.generalError.i18nKey, messages.generalError.defaults),
      )
    } catch (e) {
      setSubmitError(t(messages.generalError.i18nKey, messages.generalError.defaults))
    } finally {
      setLoading(false)
    }
  }

  const handlePrimary = () => acceptOffer(setLoadingPrimary)
  const handleSecondary = () => acceptOffer(setLoadingSecondary)
  const anyLoading = loadingPrimary || loadingSecondary
  // "Decide later" hits the same accept endpoint as "Invest", so it is gated on
  // the terms too — otherwise the checkbox would be trivially bypassable.
  const isDisabled = anyLoading || Boolean(disabled) || !termsAccepted

  return (
    <Container>
      <Header>
        <Trans {...messages.pammOfferDecideHeader} />
      </Header>
      <Description>
        <Trans {...messages.pammOfferDecideDescription} />
      </Description>
      <PammTermsCheckbox
        checked={termsAccepted}
        disabled={anyLoading || Boolean(disabled)}
        onChange={setTermsAccepted}
      />
      <ButtonsRow>
        <LoadingButton
          disabled={isDisabled}
          fullWidth={isMobile()}
          hideProgressBar={!loadingPrimary}
          id='pammOfferInvest'
          onClick={handlePrimary}
        >
          <Trans {...messages.invest} />
        </LoadingButton>
        <LoadingButton
          disabled={isDisabled}
          fullWidth={isMobile()}
          hideProgressBar={!loadingSecondary}
          id='pammOfferDecideLater'
          onClick={handleSecondary}
          variant='outlined'
        >
          <Trans {...messages.pammOfferDecideLater} />
        </LoadingButton>
      </ButtonsRow>
      <Info>
        <Trans {...messages.pammOfferPostponeInfo} />
      </Info>
      {submitError && <FormHelperText className={classes?.errorMessage}>{submitError}</FormHelperText>}
    </Container>
  )
}

export const DecideForm = withStyles(styles)(withNamespaces()(_DecideForm))
